import { Navigate, useRoutes } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';

import AuthGuard from 'src/auth/AuthGuard';
import GuestGuard from 'src/auth/GuestGuard';
import RolesGuard from 'src/auth/RolesGuard';
import { useAuthContext } from 'src/auth/useAuthContext';
import CompactLayout from 'src/layouts/compact/CompactLayout';
import DashboardLayout from 'src/layouts/dashboard';
import GuestVisitCompactLayout from 'src/layouts/guest-visit/GuestVisitCompactLayout';
import SearchCompactLayout from 'src/layouts/search/SearchCompactLayout';
import ComingSoonPage from 'src/pages/ComingSoonPage';
import AccountTabsFactPreferencesPage from 'src/pages/dashboard/AccountTabsFactPreferencesPage';
import AccountTabsGeneralPage from 'src/pages/dashboard/AccountTabsGeneralPage';
import AccountTabsPasswordPage from 'src/pages/dashboard/AccountTabsPasswordPage';
import LiveVisitPage from 'src/pages/dashboard/LiveVisitPage';
import NotificationsTabsInvitesPage from 'src/pages/dashboard/notifications/NotificationsTabsInvitesPage';
import OrganizationDetailsInvitesTabsPage from 'src/pages/dashboard/organization/OrganizationDetailsInvitesTabsPage';
import OrganizationDetailsScheduleTabsPage from 'src/pages/dashboard/organization/OrganizationDetailsScheduleTabsPage';
import OrganizationDetailsTabsPage from 'src/pages/dashboard/organization/OrganizationDetailsTabsPage';
import OrganizationPatientTabsAssistantPage from 'src/pages/dashboard/organization/OrganizationPatientTabsAssistantPage';
import OrganizationPatientTabsFilesPage from 'src/pages/dashboard/organization/OrganizationPatientTabsFilesPage';
import OrganizationPatientTabsMedicationsPage from 'src/pages/dashboard/organization/OrganizationPatientTabsMedicationsPage';
import OrganizationPatientTabsRemindersPage from 'src/pages/dashboard/organization/OrganizationPatientTabsRemindersPage';
import OrganizationPatientTabsTreatmentsPage from 'src/pages/dashboard/organization/OrganizationPatientTabsTreatmentsPage';
import OrganizationPatientTabsVisitsHistoryPage from 'src/pages/dashboard/organization/OrganizationPatientTabsVisitsHistoryPage';
import OrganizationProfessionalTabsCalendarPage from 'src/pages/dashboard/organization/OrganizationProfessionalTabsCalendarPage';
import OrganizationProfessionalTabsSchedulePage from 'src/pages/dashboard/organization/OrganizationProfessionalTabsSchedulePage';
import OrganizationProfessionalTabsVisitHistoryPage from 'src/pages/dashboard/organization/OrganizationProfessionalTabsVisitHistoryPage';
import OrganizationProfessionalTabsWaitingRoomPage from 'src/pages/dashboard/organization/OrganizationProfessionalTabsWaitingRoomPage';
import OrganizationTreatmentsPage from 'src/pages/dashboard/organization/OrganizationTreatmentsPage';
import OrganizationVisitSettingsPage from 'src/pages/dashboard/organization/OrganizationVisitSettingsPage';
import PetFactBlogPage from 'src/pages/dashboard/PetFactBlogPage';
import ProfileProductsPage from 'src/pages/dashboard/ProfileProductsPage';
import ProfileTabsFilesPage from 'src/pages/dashboard/ProfileTabsFilesPage';
import ProfileTabsMedicationsPage from 'src/pages/dashboard/ProfileTabsMedicationsPage';
import ProfileTabsRemindersPage from 'src/pages/dashboard/ProfileTabsRemindersPage';
import ProfileTabsTreatmentsPage from 'src/pages/dashboard/ProfileTabsTreatmentsPage';
import ProfileTabsVisitPage from 'src/pages/dashboard/ProfileTabsVisitPage';
import ProfileTabsVisitsHistoryPage from 'src/pages/dashboard/ProfileTabsVisitsHistoryPage';
import TemplatesPage from 'src/pages/dashboard/TemplatesPage';
import ConversationModal from 'src/sections/@dashboard/conversation/components/ConversationModal';
import { ConversationModalProvider } from 'src/sections/@dashboard/conversation/context/ConversationModalContext';
import { MicrophoneAndCameraPermissionsModalProvider } from 'src/sections/@dashboard/conversation/context/MicrophoneAndCameraPermissionsModalProvider';
import { OrganizationProvider } from 'src/sections/@dashboard/organization/context/OrganizationProvider';
import { CurrentVisitProvider } from 'src/sections/@dashboard/visit/context/CurrentVisitContext';
import VoiceReceptionistOrganizationGuard from 'src/sections/@dashboard/voice-receptionist/components/VoiceReceptionistOrganizationGuard';
import WarehouseEnabledGuard from 'src/sections/@dashboard/warehouse/components/WarehouseEnabledGuard';
import WarehouseManageGuard from 'src/sections/@dashboard/warehouse/components/WarehouseManageGuard';
import { SearchBookProvider } from 'src/sections/search/context/SearchBookProvider';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { Roles } from 'src/types/User';
import VerificationRouter from 'src/utils/verificationRouter';
import {
  AcceptTermsPage,
  AccountClosedPage,
  AccountPage,
  //
  BookingCompletedPage,
  BookingPage,
  CalendarPage,
  ConfirmationPage,
  FirstBookingPage,
  GuestVisitEntry,
  GuestManageLinkEntry,
  GuestBookingContactConfirmationEntry,
  GuestLiveVisitEntry,
  LoginPage,
  MaintenancePage,
  NotificationsPage,
  OrganizationDetailsPage,
  OrganizationPatientPage,
  OrganizationPatientsListPage,
  OrganizationProfessionalPage,
  OrganizationProfessionalsListPage,
  OrganizationStatisticsPage,
  OrganizationVerificationPage,
  Page403,
  Page404,
  //
  Page500,
  PendingDeletionPage,
  PermissionDeniedPage,
  PetsyBookingCompletedPage,
  PetsyBookingPage,
  PhoneLoginPage,
  PrivacyPolicyPage,
  ProfessionalBookingRouter,
  ProfessionalLoginPage,
  ProfessionalRegisterPage,
  ProfilePage,
  ProfilesPage,
  ProfilesVisitsHistoryPage,
  RegisterPage,
  ResetPasswordPage,
  ResetPasswordRequestSentPage,
  SearchBookingPage,
  SearchResultsDetailsPage,
  SearchResultsPage,
  StatisticsPage,
  TermsConditionsPage,
  TreatmentsOverviewPage,
  UpcomingVisitsPage,
  UpdateAccountPage,
  VisitNotesPage,
  VisitPage,
  VisitsHistoryPage,
  WaitingRoomPage,
  WarehouseDemandTab,
  WarehouseSalesTab,
  WarehouseDocumentsTab,
  WarehousePage,
  WarehouseProductsTab,
  WarehouseSuppliersTab,
  VoiceReceptionistDashboardPage,
  VoiceReceptionistCallPage,
  VoicePrivacyNoticePage,
  VoiceDevelopOperatorTrialNoticePage,
  VoicePrivacyRequestPage,
  WebinarAdminRedirectPage,
  WebinarPresenterPage,
} from './elements';

// ----------------------------------------------------------------------

/**
 * The tab routes under the professional's live visit page.
 *
 * Both variants of the route mount the same set: the tab bodies resolve their
 * own data scope (profile if the patient has one, otherwise the visit), so the
 * only real difference is Historia, which needs a profile to have a history.
 */
function buildVisitChildRoutes({
  includeHistory,
}: {
  includeHistory: boolean;
}): RouteObject[] {
  return [
    {
      // Notes are what a professional opens a visit to write, so the visit
      // lands there. Every tab keeps an explicit path; the index only points
      // at the default one.
      index: true,
      element: <Navigate to="notes" replace />,
    },
    { path: 'notes', element: <VisitNotesPage /> },
    { path: 'details', element: <ProfileTabsVisitPage /> },
    ...(includeHistory
      ? [
          {
            path: 'history',
            element: <OrganizationPatientTabsVisitsHistoryPage />,
          },
        ]
      : []),
    {
      path: 'medications',
      element: <OrganizationPatientTabsMedicationsPage />,
    },
    { path: 'reminders', element: <OrganizationPatientTabsRemindersPage /> },
    { path: 'treatments', element: <OrganizationPatientTabsTreatmentsPage /> },
    { path: 'files', element: <OrganizationPatientTabsFilesPage /> },
    { path: '*', element: <Navigate to="." replace /> },
  ];
}

// ----------------------------------------------------------------------

export default function Router(): JSX.Element | null {
  const { user } = useAuthContext();
  const passwordProvider = user?.providerData[0]?.providerId === 'password';

  return useRoutes([
    {
      path: 'voice-privacy/:organizationId',
      element: <VoicePrivacyNoticePage />,
    },
    // Assignment-authorized: any verified account, without clinic onboarding.
    {
      path: 'webinars/presenter',
      element: (
        <OrganizationProvider>
          <AuthGuard requireOnboarding={false}>
            <CompactLayout />
          </AuthGuard>
        </OrganizationProvider>
      ),
      children: [{ index: true, element: <WebinarPresenterPage /> }],
    },
    {
      path: 'voice-privacy/:organizationId/develop-operator-trial',
      element: <VoiceDevelopOperatorTrialNoticePage />,
    },
    {
      path: 'voice-privacy/:organizationId/request',
      element: <VoicePrivacyRequestPage />,
    },
    // Auth
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
        {
          path: 'phone-login',
          element: (
            <GuestGuard>
              <PhoneLoginPage />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <RegisterPage />
            </GuestGuard>
          ),
        },
        {
          path: 'professional-register',
          element: (
            <GuestGuard>
              <ProfessionalRegisterPage />
            </GuestGuard>
          ),
        },
        {
          path: 'professional-login',
          element: (
            <GuestGuard>
              <ProfessionalLoginPage />
            </GuestGuard>
          ),
        },
        {
          path: 'confirmation',
          element: (
            <GuestGuard>
              <ConfirmationPage />
            </GuestGuard>
          ),
        },
        {
          path: 'verify',
          element: (
            <GuestGuard>
              <VerificationRouter />
            </GuestGuard>
          ),
        },
        {
          path: 'reset-password',
          element: <ResetPasswordPage />,
        },
        { path: 'request-sent', element: <ResetPasswordRequestSentPage /> },
        {
          path: 'accept-terms',
          element: <AcceptTermsPage />,
        },
        {
          path: 'account-closed',
          element: <AccountClosedPage />,
        },
        {
          path: 'pending-deletion',
          element: <PendingDeletionPage />,
        },
        {
          path: 'terms-conditions/user',
          element: <TermsConditionsPage />,
        },
        {
          path: 'terms-conditions/professional',
          element: <TermsConditionsPage />,
        },
        {
          path: 'privacy-policy',
          element: <PrivacyPolicyPage />,
        },
        {
          path: 'privacy-policy/user',
          element: <PrivacyPolicyPage />,
        },
        {
          path: 'privacy-policy/professional',
          element: <PrivacyPolicyPage />,
        },
      ],
    },

    // Search
    {
      path: 'search',
      element: (
        <GuestGuard>
          <SearchBookProvider>
            <SearchCompactLayout></SearchCompactLayout>
          </SearchBookProvider>
        </GuestGuard>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="results" replace />,
        },
        {
          path: 'results',
          element: <SearchResultsPage />,
        },
        {
          path: 'results/:organizationId',
          element: <SearchResultsDetailsPage />,
        },
        {
          path: 'results/:organizationId/booking',
          element: <SearchBookingPage />,
        },
        {
          path: 'results/:organizationId/booking/completed/:visitId',
          element: <BookingCompletedPage />,
        },
        {
          path: '*',
          element: <Navigate to="results" replace />,
        },
      ],
    },
    {
      path: 'placowka',
      element: (
        <GuestGuard>
          <SearchBookProvider>
            <SearchCompactLayout />
          </SearchBookProvider>
        </GuestGuard>
      ),
      children: [
        {
          path: ':organizationId',
          element: <SearchResultsDetailsPage />,
        },
      ],
    },
    // Dashboard
    {
      path: 'dashboard',
      element: (
        <OrganizationProvider>
          <AuthGuard>
            <MicrophoneAndCameraPermissionsModalProvider>
              <CurrentVisitProvider>
                <ConversationModalProvider>
                  <ConversationModal />
                  <DashboardLayout />
                </ConversationModalProvider>
              </CurrentVisitProvider>
            </MicrophoneAndCameraPermissionsModalProvider>
          </AuthGuard>
        </OrganizationProvider>
      ),
      children: [
        { path: 'update-account', element: <UpdateAccountPage /> },
        {
          path: 'update-account/invitation',
          element: <UpdateAccountPage invitationStep />,
        },
        {
          path: 'organization-verification',
          element: <OrganizationVerificationPage />,
        },
        {
          path: 'account',
          element: <AccountPage />,
          children: [
            { path: 'general', element: <AccountTabsGeneralPage /> },
            ...(passwordProvider
              ? [{ path: 'password', element: <AccountTabsPasswordPage /> }]
              : []),
            {
              path: 'fact-preferences',
              element: (
                <RolesGuard
                  roles={[Roles.user]}
                  onPermissionDenied={() => <Navigate to="general" />}
                >
                  <AccountTabsFactPreferencesPage />
                </RolesGuard>
              ),
            },
            { path: '*', element: <Navigate to="general" /> },
          ],
        },
        { path: 'pet-facts/:factId', element: <PetFactBlogPage /> },
        // {
        //   path: 'book',
        //   element: (
        //     <RolesGuard roles={[Roles.user]}>
        //       <BookPage />
        //     </RolesGuard>
        //   ),
        // },
        {
          path: 'profiles',
          element: (
            <RolesGuard roles={[Roles.user]}>
              <ProfilesPage />
            </RolesGuard>
          ),
        },
        {
          path: 'upcoming-visits',
          element: (
            <RolesGuard roles={[Roles.user]}>
              <UpcomingVisitsPage />
            </RolesGuard>
          ),
        },
        {
          path: 'history',
          element: (
            <RolesGuard roles={[Roles.user]}>
              <ProfilesVisitsHistoryPage />
            </RolesGuard>
          ),
        },
        {
          path: 'profiles/:profileId',
          element: <ProfilePage />,
          children: [
            {
              path: 'visits-history',
              element: <ProfileTabsVisitsHistoryPage />,
            },
            { path: 'files', element: <ProfileTabsFilesPage /> },
            { path: 'medications', element: <ProfileTabsMedicationsPage /> },
            { path: 'reminders', element: <ProfileTabsRemindersPage /> },
            { path: 'treatments', element: <ProfileTabsTreatmentsPage /> },
            { path: 'products', element: <ProfileProductsPage /> },
            {
              path: '*',
              element: <Navigate to="visits-history" />,
            },
          ],
        },
        {
          path: 'booking/schedule/:profileId',
          element: (
            <RolesGuard
              roles={[Roles.user]}
              onPermissionDenied={() => <Navigate to="waiting-room" />}
            >
              <BookingPage />
            </RolesGuard>
          ),
        },
        {
          path: 'booking/first-schedule',
          element: (
            <RolesGuard
              roles={[Roles.user]}
              onPermissionDenied={() => <Navigate to="waiting-room" />}
            >
              <FirstBookingPage />
            </RolesGuard>
          ),
        },
        {
          path: 'booking/professional/:professionalId',
          element: <ProfessionalBookingRouter />,
        },
        {
          path: 'booking/schedule/:profileId/:visitId',
          element: (
            <RolesGuard
              roles={[Roles.user]}
              onPermissionDenied={() => <Navigate to="waiting-room" />}
            >
              <BookingPage />
            </RolesGuard>
          ),
        },
        // PETSY ROUTES
        {
          path: 'booking/petsy-schedule/:profileId',
          element: (
            <RolesGuard
              roles={[Roles.user]}
              onPermissionDenied={() => <Navigate to="waiting-room" />}
            >
              <PetsyBookingPage />
            </RolesGuard>
          ),
        },
        {
          path: 'booking/petsy-schedule/:profileId/:visitId',
          element: (
            <RolesGuard
              roles={[Roles.user]}
              onPermissionDenied={() => <Navigate to="waiting-room" />}
            >
              <PetsyBookingPage />
            </RolesGuard>
          ),
        },
        {
          path: 'booking/petsy-completed',
          element: (
            <RolesGuard
              roles={[Roles.user]}
              onPermissionDenied={() => <Navigate to="waiting-room" />}
            >
              <BookingCompletedPage />
            </RolesGuard>
          ),
        },
        // Przelewy24
        {
          path: 'booking/petsy-completed/:visitId',
          element: (
            <RolesGuard
              roles={[Roles.user]}
              onPermissionDenied={() => <Navigate to="waiting-room" />}
            >
              <PetsyBookingCompletedPage />
            </RolesGuard>
          ),
        },
        // Autopay
        {
          path: 'booking/completed',
          element: (
            <RolesGuard
              roles={[Roles.user]}
              onPermissionDenied={() => <Navigate to="waiting-room" />}
            >
              <PetsyBookingCompletedPage />
            </RolesGuard>
          ),
        },
        // Przelewy24
        {
          path: 'booking/completed/:visitId',
          element: (
            <RolesGuard
              roles={[Roles.user]}
              onPermissionDenied={() => <Navigate to="waiting-room" />}
            >
              <BookingCompletedPage />
            </RolesGuard>
          ),
        },
        {
          path: 'live-visit/:visitId',
          element: (
            <RolesGuard
              roles={[Roles.user]}
              onPermissionDenied={() => <Navigate to="waiting-room" />}
            >
              <LiveVisitPage />
            </RolesGuard>
          ),
        },
        {
          path: 'waiting-room',
          element: (
            <RolesGuard
              roles={[
                Roles.professional,
                Roles.organization_admin,
                Roles.organization_owner,
              ]}
              onPermissionDenied={() => <Navigate to="profiles" />}
            >
              <WaitingRoomPage />
            </RolesGuard>
          ),
        },
        {
          path: 'visit/:visitId',
          element: (
            <RolesGuard
              roles={[Roles.professional]}
              onPermissionDenied={() => <Navigate to="waiting-room" />}
            >
              <VisitPage />
            </RolesGuard>
          ),
          // A guest visit has no profile, so no patient history to show.
          children: buildVisitChildRoutes({ includeHistory: false }),
        },
        {
          path: 'visit/:visitId/patient/:patientId',
          element: (
            <RolesGuard
              roles={[Roles.professional]}
              onPermissionDenied={() => <Navigate to="waiting-room" />}
            >
              <VisitPage />
            </RolesGuard>
          ),
          children: buildVisitChildRoutes({ includeHistory: true }),
        },
        {
          path: 'visits-history',
          element: (
            <RolesGuard
              roles={[
                Roles.professional,
                Roles.organization_admin,
                Roles.organization_owner,
              ]}
              onPermissionDenied={() => <Navigate to="profiles" />}
            >
              <VisitsHistoryPage />
            </RolesGuard>
          ),
        },
        {
          path: 'statistics',
          element: (
            <RolesGuard
              roles={[
                Roles.professional,
                Roles.organization_admin,
                Roles.organization_owner,
              ]}
              onPermissionDenied={() => <Navigate to="profiles" />}
            >
              <StatisticsPage />
            </RolesGuard>
          ),
        },
        {
          path: 'voice-reception',
          element: (
            <RolesGuard
              roles={[Roles.organization_admin, Roles.organization_owner]}
              onPermissionDenied={() => <Navigate to="/dashboard/calendar" />}
            >
              <VoiceReceptionistOrganizationGuard>
                <VoiceReceptionistDashboardPage />
              </VoiceReceptionistOrganizationGuard>
            </RolesGuard>
          ),
        },
        {
          path: 'voice-reception/calls/:callRef',
          element: (
            <RolesGuard
              roles={[Roles.organization_admin, Roles.organization_owner]}
              onPermissionDenied={() => <Navigate to="/dashboard/calendar" />}
            >
              <VoiceReceptionistOrganizationGuard>
                <VoiceReceptionistCallPage />
              </VoiceReceptionistOrganizationGuard>
            </RolesGuard>
          ),
        },
        {
          path: 'voice-reception/*',
          element: (
            <Navigate
              to={PATH_DASHBOARD.general.voiceReceptionist.root}
              replace
            />
          ),
        },
        {
          path: 'calendar',
          element: (
            <RolesGuard
              roles={[
                Roles.professional,
                Roles.organization_admin,
                Roles.organization_owner,
              ]}
              onPermissionDenied={() => <Navigate to="profiles" />}
            >
              <CalendarPage />
            </RolesGuard>
          ),
        },
        {
          path: 'visits',
          children: [
            { path: 'live/:visitId', element: <LiveVisitPage /> },
            { path: 'waiting-room/:visitId', element: <WaitingRoomPage /> },
            {
              path: 'history',
              element: <VisitsHistoryPage />,
            },
            {
              path: 'history/:visitId',
              element: <ProfileTabsVisitPage />,
            },
          ],
        },
        // Legacy platform-admin links now open the admin app.
        {
          path: 'webinars',
          element: (
            <RolesGuard roles={[Roles.admin]}>
              <WebinarAdminRedirectPage />
            </RolesGuard>
          ),
        },
        {
          path: 'webinars/:webinarId',
          element: (
            <RolesGuard roles={[Roles.admin]}>
              <WebinarAdminRedirectPage />
            </RolesGuard>
          ),
        },
        // ---ORG ADMIN ROUTES---
        {
          path: 'organization/:organizationId',
          element: (
            <RolesGuard
              roles={[
                Roles.organization_admin,
                Roles.organization_owner,
                Roles.professional,
              ]}
            >
              <OrganizationDetailsPage />
            </RolesGuard>
          ),
          children: [
            {
              path: 'schedule',
              element: <OrganizationDetailsScheduleTabsPage />,
            },
            {
              path: 'visit-settings',
              element: <OrganizationVisitSettingsPage />,
            },
            {
              path: 'details',
              element: <OrganizationDetailsTabsPage />,
            },
            {
              path: 'invites',
              element: <OrganizationDetailsInvitesTabsPage />,
            },
            {
              path: 'treatments',
              element: <OrganizationTreatmentsPage />,
            },
            {
              path: 'statistics',
              element: (
                <RolesGuard
                  roles={[Roles.organization_admin, Roles.organization_owner]}
                  onPermissionDenied={() => <Navigate to="details" />}
                >
                  <OrganizationStatisticsPage />
                </RolesGuard>
              ),
            },
            {
              path: '*',
              element: <Navigate to="details" />,
            },
          ],
        },
        {
          path: 'professionals',
          element: (
            <RolesGuard
              roles={[Roles.organization_admin, Roles.organization_owner]}
            >
              <OrganizationProfessionalsListPage />
            </RolesGuard>
          ),
        },
        {
          path: 'professionals/:professionalId',
          element: (
            <RolesGuard
              roles={[
                Roles.organization_admin,
                Roles.organization_owner,
                Roles.professional,
              ]}
            >
              <OrganizationProfessionalPage />
            </RolesGuard>
          ),
          children: [
            {
              path: 'calendar',
              element: <OrganizationProfessionalTabsCalendarPage />,
            },
            {
              path: 'waiting-room',
              element: <OrganizationProfessionalTabsWaitingRoomPage />,
            },
            {
              path: 'history',
              element: <OrganizationProfessionalTabsVisitHistoryPage />,
            },
            {
              path: 'schedule',
              element: <OrganizationProfessionalTabsSchedulePage />,
            },
            {
              path: '*',
              element: <Navigate to="history" />,
            },
          ],
        },
        {
          path: 'patients',
          element: (
            <RolesGuard
              roles={[
                Roles.organization_admin,
                Roles.organization_owner,
                Roles.professional,
              ]}
            >
              <OrganizationPatientsListPage />
            </RolesGuard>
          ),
        },
        {
          path: 'patients/:patientId',
          element: (
            <RolesGuard
              roles={[
                Roles.organization_admin,
                Roles.organization_owner,
                Roles.professional,
              ]}
            >
              <OrganizationPatientPage />
            </RolesGuard>
          ),
          children: [
            {
              path: 'assistant',
              element: (
                <RolesGuard
                  roles={[Roles.professional]}
                  onPermissionDenied={() => <Navigate to="history" />}
                >
                  <OrganizationPatientTabsAssistantPage />
                </RolesGuard>
              ),
            },
            {
              path: 'history',
              element: <OrganizationPatientTabsVisitsHistoryPage />,
            },
            {
              path: 'medications',
              element: <OrganizationPatientTabsMedicationsPage />,
            },
            {
              path: 'reminders',
              element: <OrganizationPatientTabsRemindersPage />,
            },
            {
              path: 'treatments',
              element: <OrganizationPatientTabsTreatmentsPage />,
            },
            {
              path: 'files',
              element: <OrganizationPatientTabsFilesPage />,
            },
            {
              path: '*',
              element: <Navigate to="history" />,
            },
          ],
        },
        {
          path: 'treatments',
          element: (
            <RolesGuard
              roles={[
                Roles.organization_admin,
                Roles.organization_owner,
                Roles.professional,
              ]}
            >
              <TreatmentsOverviewPage />
            </RolesGuard>
          ),
        },
        {
          path: 'warehouse',
          element: (
            <RolesGuard
              roles={[
                Roles.organization_admin,
                Roles.organization_owner,
                Roles.professional,
              ]}
            >
              <WarehousePage />
            </RolesGuard>
          ),
          children: [
            { index: true, element: <Navigate to="products" replace /> },
            { path: 'products', element: <WarehouseProductsTab /> },
            { path: 'sales', element: <WarehouseSalesTab /> },
            { path: 'documents', element: <WarehouseDocumentsTab /> },
            {
              path: 'demand',
              element: (
                <WarehouseEnabledGuard>
                  <WarehouseDemandTab />
                </WarehouseEnabledGuard>
              ),
            },
            {
              path: 'suppliers',
              element: (
                <RolesGuard
                  roles={[Roles.organization_admin, Roles.organization_owner]}
                  onPermissionDenied={() => (
                    <Navigate to={PATH_DASHBOARD.warehouse.products} replace />
                  )}
                >
                  <WarehouseManageGuard>
                    <WarehouseSuppliersTab />
                  </WarehouseManageGuard>
                </RolesGuard>
              ),
            },
          ],
        },
        // ---ORG ADMIN ROUTES---
        {
          path: 'notifications',
          element: (
            <RolesGuard
              roles={[
                Roles.user,
                Roles.admin,
                Roles.organization_admin,
                Roles.organization_owner,
                Roles.professional,
              ]}
            >
              <NotificationsPage />
            </RolesGuard>
          ),
          children: [
            { index: true, element: <Navigate to="invites" replace /> },
            {
              path: 'invites',
              element: <NotificationsTabsInvitesPage />,
            },
          ],
        },
        { path: 'templates', element: <TemplatesPage /> },
        // { path: 'forms', element: <FormsPage /> },
        // { path: 'forms/new', element: <FormBuilderPage /> },
        // { path: 'forms/:formId', element: <FormBuilderPage /> },
        // { path: 'visits/:visitId/forms/:formId', element: <FormFillPage /> },
        // Authenticated Search Routes (use DashboardLayout, not SearchCompactLayout)
        {
          path: 'search',
          element: (
            <SearchBookProvider>
              <Navigate to="results" replace />
            </SearchBookProvider>
          ),
        },
        {
          path: 'search/results',
          element: (
            <SearchBookProvider>
              <SearchResultsPage />
            </SearchBookProvider>
          ),
        },
        {
          path: 'search/results/:organizationId',
          element: (
            <SearchBookProvider>
              <SearchResultsDetailsPage />
            </SearchBookProvider>
          ),
        },
        {
          path: 'search/results/:organizationId/booking',
          element: (
            <SearchBookProvider>
              <SearchBookingPage />
            </SearchBookProvider>
          ),
        },
        {
          path: 'placowka/:organizationId',
          element: (
            <SearchBookProvider>
              <SearchResultsDetailsPage />
            </SearchBookProvider>
          ),
        },
        { path: '404', element: <Page404 /> },
        { path: 'permission-denied', element: <PermissionDeniedPage /> },
        { path: '401', element: <Page403 /> },
        { path: '*', element: <Navigate to="/dashboard" replace /> },
      ],
    },
    {
      element: <CompactLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoonPage /> },
        { path: 'maintenance', element: <MaintenancePage /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
      ],
    },
    // GUEST VISIT (public, no auth — guard handles its own verify flow)
    // Wrapped in GuestVisitCompactLayout — visually parallel to the search
    // layout but a separate file so guest-visit chrome can evolve
    // independently. The layout renders an <Outlet />, so a child route
    // preserves the original /visit/guest/:visitId URL.
    {
      path: 'g/:handle',
      element: <GuestVisitCompactLayout />,
      children: [{ index: true, element: <GuestManageLinkEntry /> }],
    },
    {
      path: 'c/:channel/:visitId/:handle',
      element: <GuestVisitCompactLayout />,
      children: [
        { index: true, element: <GuestBookingContactConfirmationEntry /> },
      ],
    },
    {
      path: 'visit/guest/:visitId',
      element: <GuestVisitCompactLayout />,
      children: [
        { index: true, element: <GuestVisitEntry /> },
        { path: 'live', element: <GuestLiveVisitEntry /> },
      ],
    },
    // Main Routes
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
    {
      path: '/',
      element: <Navigate to="/auth/login" />,
    },
  ]);
}
